import {loggerFactory} from "@cm/browser-log"
import type {CmLogger} from "@cm/log"
import type {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {ImageOpHalOutputImage} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image"
import {ImageOpHalOutputImagePhysical} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image-physical"
import {ImageOpHalOutputImageVirtual} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-output-image-virtual"
import {CachingStrategy, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {ImageOpDefinition, SkipMappingTypes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {DataType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {Descriptor, MipLevelRegion, MipLevelRegions, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {traverse} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {mergeRegionsInplace} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {EvaluatedParameters} from "@cm/graph-v2-final"
import {getMipLevelSize, getNumMipLevels, HalImage, PageDescriptor} from "@cm/hal"
import {assertNever, deepCopy, deepEqual, REF_COUNT_DEBUG_ID_INITIAL, ReplaceAWithB, TrackedRefCountableBase} from "@cm/utils"
import {Subscription} from "rxjs"

const ENABLE_DEBUG_INFO = true

export type MapToDataType<T> = ReplaceAWithB<T, AnyImageOpHal, AnyImageOpHalData, SkipMappingTypes | ImageOpImgProc>

export type AnyImageOpHalData = ImageOpHalData<ImageOpDefinition>
export type ImageOpHalDataParameters<Definition extends ImageOpDefinition> = EvaluatedParameters<MapToDataType<ImageOpParametersOfType<Definition, "hal">>>

export type OutputImageReuseType = "reuse-always" | "reuse-never" | "reuse-if-same-descriptor" | "no-output-image"

export type CtorArgs<Definition extends ImageOpDefinition, ImageOpHalDataClass extends ImageOpHalData<Definition>> = {
    ctx: ImageOpContextHal
    parameters: ImageOpHalDataParameters<Definition>
    descriptor: Descriptor
    prevData: ImageOpHalDataClass | null
    outputImageReuse: OutputImageReuseType
    updateComplexity: number // higher means more complex
    cachingStrategy?: CachingStrategy // default: "auto"
    invalidateEntireOutputOnParameterChange?: boolean // default: true
}

const REF_COUNT_DEBUG_ID_OUTPUT_IMAGE = "ImageOpHalData.outputImagePtr"

export abstract class ImageOpHalData<Definition extends ImageOpDefinition> extends TrackedRefCountableBase {
    protected abstract onSourceRegionsInvalidated(sourceImage: AnyImageOpHalData, sourceRegions: Regions): void

    protected abstract writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void>

    readonly logger: CmLogger

    constructor(args: CtorArgs<Definition, ImageOpHalData<Definition>>) {
        super({
            disposeFn: () => this.dispose(),
        })
        this.ctx = args.ctx
        this.logger = args.ctx.logger
        this._cachingStrategy = args.cachingStrategy ?? "auto"
        this.parameters = deepCopy(args.parameters)
        this.descriptor = deepCopy(args.descriptor)
        this.numMipLevels = getNumMipLevels(this.descriptor)
        const imageOpHalOptions = this.parameters.imageOpHalOptions
        const prevData = args.prevData
        this._usePhysicalOutputImage = imageOpHalOptions?.usePhysicalOutputImage ?? false
        this._physicalOutputImageDataType = imageOpHalOptions?.physicalOutputDataType ?? "float32"
        this._updateComplexity = args.updateComplexity
        if (args.outputImageReuse !== "no-output-image") {
            let canReusePrevOutputImage: boolean
            switch (args.outputImageReuse) {
                case "reuse-always":
                    canReusePrevOutputImage = true
                    break
                case "reuse-never":
                    canReusePrevOutputImage = false
                    break
                case "reuse-if-same-descriptor":
                    canReusePrevOutputImage = !!prevData && deepEqual(prevData.descriptor, this.descriptor)
                    break
                default:
                    assertNever(args.outputImageReuse)
            }
            if (canReusePrevOutputImage && prevData?._outputImagePtr) {
                this._outputImagePtr = prevData._outputImagePtr
                this._outputImagePtr.addRef(REF_COUNT_DEBUG_ID_OUTPUT_IMAGE)
            } else {
                this._outputImagePtr = this.allocateHalOutput(this.descriptor)
                this._outputImagePtr.addRef(REF_COUNT_DEBUG_ID_OUTPUT_IMAGE)
                this._outputImagePtr.release(REF_COUNT_DEBUG_ID_INITIAL)
            }
        } else {
            this._outputImagePtr = null
        }
        this.setSourceImages(this.collectSourceImages())
        const invalidateEntireOutputOnParameterChange = args.invalidateEntireOutputOnParameterChange ?? true
        const parametersChanged = args.prevData ? this.anyParametersChanges(args.prevData.parameters) : false
        if (args.prevData && invalidateEntireOutputOnParameterChange && parametersChanged) {
            // if any parameters changed, invalidate the entire output
            this.invalidateRegions()
        } else {
            const prevSourceImages = args.prevData?.sourceImages
            if (prevSourceImages && prevSourceImages.length !== this._sourceImages.length) {
                // Some source images were removed or added; the entire output is no longer valid.
                this.invalidateRegions()
            } else {
                for (const sourceImage of this._sourceImages) {
                    if (sourceImage._initiallyInvalidRegions.length > 0) {
                        // invalidate the requested regions
                        this.onSourceRegionsInvalidated(sourceImage, sourceImage._initiallyInvalidRegions)
                    } else if (prevSourceImages && !prevSourceImages.includes(sourceImage)) {
                        // This source image is new (not present in prevData's sources) and has no
                        // initial invalid regions. This happens when the operator graph topology
                        // changes (e.g. an upstream operator is enabled or disabled), causing a
                        // different source to feed this node. Conservatively invalidate everything.
                        this.invalidateRegions()
                        break
                    }
                }
            }
        }
        ImageOpHalData.addInstance(this)
    }

    protected dispose(): void {
        this.checkNotDisposed()
        this._disposed = true
        this.setSourceImages([])
        this._outputImagePtr?.release(REF_COUNT_DEBUG_ID_OUTPUT_IMAGE)
        this._outputImagePtr = null
        ImageOpHalData.removeInstance(this)
    }

    private anyParametersChanges(prevParameters: ImageOpHalDataParameters<Definition>) {
        return !this.deepEqualParameters(this.parameters, prevParameters)
    }

    private deepEqualParameters<T>(a: T, b: T): boolean {
        const typeA = typeof a
        const typeB = typeof b
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (typeA === "number" && typeB === "number" && isNaN(a as any) && isNaN(b as any)) return true
        else if (a === b) return true
        else if (a && b && typeA === "object" && typeB === "object") {
            if (Array.isArray(a) && Array.isArray(b)) {
                const length = a.length
                if (length != b.length) return false
                for (let i = length; i-- !== 0; ) {
                    if (!this.deepEqualParameters(a[i], b[i])) return false
                }
                return true
            } else {
                // ignore data parameters as they might change without the actual data changing
                if (!(a instanceof ImageOpHalData) && !(b instanceof ImageOpHalData)) {
                    const keys = Object.keys(a as unknown as object) as (keyof T)[]
                    const length = keys.length
                    if (length !== Object.keys(b as unknown as object).length) return false
                    for (const k of keys) {
                        if (!(k in (b as unknown as object))) return false
                        if (!this.deepEqualParameters(a[k], b[k])) return false
                    }
                }
                return true
            }
        } else {
            return false
        }
    }

    set debugId(value: string | undefined) {
        this._debugId = value
        if (this._outputImagePtr) {
            this._outputImagePtr.debugId = value
        }
    }

    get debugId() {
        return this._debugId
    }

    get usePhysicalOutputImage() {
        return this._usePhysicalOutputImage
    }

    getMipLevelSize(mipLevel: number) {
        return getMipLevelSize(this.descriptor, mipLevel)
    }

    get hasOutputImage() {
        return !!this._outputImagePtr
    }

    get outputImagePtr() {
        this.checkNotDisposed()
        if (!this._outputImagePtr) {
            throw new Error("This ImageOpHalData does not have an output image")
        }
        return this._outputImagePtr
    }

    private allocateHalOutput(descriptor: Descriptor): ImageOpHalOutputImage {
        if (this._usePhysicalOutputImage) {
            return new ImageOpHalOutputImagePhysical(this.ctx, descriptor, this._physicalOutputImageDataType)
        } else {
            return new ImageOpHalOutputImageVirtual(this.ctx, descriptor)
        }
    }

    private updatePagePriority() {
        const outputImage = this._outputImagePtr
        if (!outputImage || !(outputImage instanceof ImageOpHalOutputImageVirtual)) {
            return
        }
        const pagePriority = this.computePagePriority()
        outputImage.setPagePriority(pagePriority)
    }

    private computePagePriority(): number {
        return 1 // simple for now
        // const C = this.computationScore
        // const R = this.reusabilityScore
        // const V = this.volatilityScore
        // return (C * R) / (1 + V)
    }

    private get computationScore() {
        return this._updateComplexity
    }

    private get reusabilityScore(): number {
        return this.consumers.size
    }

    private get volatilityScore(): number {
        return 0
    }

    protected invalidateRegions(regions?: Regions) {
        regions ??= [
            {
                x: 0,
                y: 0,
                width: this.descriptor.width,
                height: this.descriptor.height,
            },
        ]
        if (regions.length === 0) {
            return
        }
        this._outputImagePtr?.invalidateRegions(regions)
        this._initiallyInvalidRegions.push(...regions)
        mergeRegionsInplace(this._initiallyInvalidRegions)
    }

    private checkNotDisposed() {
        if (this._disposed) {
            throw new Error("Attempting to access disposed ImageOpHal")
        }
    }

    private addConsumer(consumer: AnyImageOpHalData) {
        if (this.consumers.has(consumer)) {
            throw new Error("Consumer already added")
        }
        this.consumers.add(consumer)
    }

    private removeConsumer(consumer: AnyImageOpHalData) {
        if (!this.consumers.has(consumer)) {
            throw new Error("Consumer not found")
        }
        this.consumers.delete(consumer)
    }

    get cachingEnabled() {
        return this._cachingStrategy === "always" || (this._cachingStrategy === "auto" && this._cachingIsUseful)
    }

    // requesting an out-of-bounds region will return a wrapped region
    async getUpdatedHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        this.logger.trace(
            `Requested updated region [${mipLevelRegion.x}, ${mipLevelRegion.y}, ${mipLevelRegion.width}, ${mipLevelRegion.height}] on mip level ${mipLevelRegion.mipLevel} for HalImage ${this.debugId}`,
            this,
        )

        // clamp the mip-level
        if (mipLevelRegion.mipLevel >= this.numMipLevels) {
            this.ctx.logger.warn(`Mip level ${mipLevelRegion.mipLevel} is out of bounds, clamping to ${this.numMipLevels - 1}`)
            const mipLevelCorrection = mipLevelRegion.mipLevel - this.numMipLevels + 1
            const mipLevelCorrectionScale = 1 << mipLevelCorrection
            mipLevelRegion.x *= mipLevelCorrectionScale
            mipLevelRegion.y *= mipLevelCorrectionScale
            mipLevelRegion.width *= mipLevelCorrectionScale
            mipLevelRegion.height *= mipLevelCorrectionScale
            mipLevelRegion.mipLevel -= mipLevelCorrection
        }

        // limit the region to the mip-level size
        const mipLevelSize = getMipLevelSize(this.descriptor, mipLevelRegion.mipLevel)
        mipLevelRegion = {
            x: mipLevelRegion.x,
            y: mipLevelRegion.y,
            width: Math.min(mipLevelRegion.width, mipLevelSize.width),
            height: Math.min(mipLevelRegion.height, mipLevelSize.height),
            mipLevel: mipLevelRegion.mipLevel,
        }

        this.updatePagePriority()

        return this.updateHalImage(mipLevelRegion, options)
    }

    protected async updateHalImage(mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<RegionLockedHalImageRef> {
        this.addRef("ImageOpHalData.getUpdatedHalImage") // ensure we are not disposed while an update is pending
        try {
            const writeRegionFn = (outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions) => {
                this.checkNotDisposed()
                return this.writeRegion(outputImage, mipLevelRegion, options)
            }
            const subSampleRegionFn = (targetImage: HalImage, sourceImage: HalImage, mipLevelRegion: MipLevelRegion) => {
                this.checkNotDisposed()
                return this.subSampleRegion(targetImage, sourceImage, mipLevelRegion)
            }
            const pendingPromise = this.outputImagePtr.getUpdatedHalImage(writeRegionFn, subSampleRegionFn, mipLevelRegion, {
                ...options,
                cachingEnabled: this.cachingEnabled,
            })
            const debugObj: PendingPromiseDebugInfo = {
                promise: pendingPromise,
                imageOpHalData: this,
                mipLevelRegions: [mipLevelRegion],
            }
            if (this.logger.isEnabled("debug")) {
                ImageOpHalData.pendingPromises.add(debugObj)
            }

            const updatedRegion = await pendingPromise

            if (this.logger.isEnabled("debug")) {
                ImageOpHalData.pendingPromises.delete(debugObj)
            }

            return updatedRegion
        } finally {
            this.release("ImageOpHalData.getUpdatedHalImage")
        }
    }

    protected subSampleRegion(targetImage: HalImage, sourceImage: HalImage, mipLevelRegion: MipLevelRegion) {
        if (mipLevelRegion.mipLevel < 1 || mipLevelRegion.mipLevel >= targetImage.descriptor.numMipLevels) {
            throw new Error("Invalid mip level")
        }
        this.ctx.mipmapHelper.generateMipMap({
            source: sourceImage,
            target: targetImage,
            options: {
                targetRegion: mipLevelRegion,
                targetMipLevel: mipLevelRegion.mipLevel,
                sourceMipLevel: mipLevelRegion.mipLevel - 1,
                // smoothing: "binomial6",
            },
        })
    }

    checkNoLockedPages() {
        if (this._outputImagePtr instanceof ImageOpHalOutputImageVirtual) {
            const lockedPages = this._outputImagePtr.getLockedPages()
            if (lockedPages.length > 0) {
                this.logger.warn(`Found ${lockedPages.length} locked pages for ${this.constructor.name} ${this.debugId}`, this, lockedPages)
                return true
            }
        }
        return false
    }

    getNumAllocatedPages() {
        if (this._outputImagePtr instanceof ImageOpHalOutputImageVirtual) {
            const stats = this._outputImagePtr.getStats()
            return stats.allocatedPages.length * stats.numAtlasPagesPerVirtualPage
        } else {
            return 0
        }
    }

    printDebugInfo() {
        ImageOpHalData.logger.log(`    Parameters: `, this.parameters)
        ImageOpHalData.logger.log(`    Descriptor: `, this.descriptor)
        if (this._outputImagePtr instanceof ImageOpHalOutputImageVirtual) {
            const stats = this._outputImagePtr.getStats()
            ImageOpHalData.logger.log(
                `    Allocated pages (${stats.allocatedPages.length} virtual pages${stats.sharedPages.length > 0 ? ` (${stats.sharedPages.length} shared)` : ""} / ${stats.allocatedPages.length * stats.numAtlasPagesPerVirtualPage} atlas pages): `,
            )
            // separate out allocated pages per mip level
            const pagesByMipLevel = new Map<number, PageDescriptor[]>()
            let minMipLevel = Number.MAX_SAFE_INTEGER
            let maxMipLevel = 0
            stats.allocatedPages.forEach((pageDescriptor) => {
                let mipLevelPages = pagesByMipLevel.get(pageDescriptor.mipLevel)
                if (!mipLevelPages) {
                    mipLevelPages = []
                    pagesByMipLevel.set(pageDescriptor.mipLevel, mipLevelPages)
                }
                mipLevelPages.push(pageDescriptor)
                minMipLevel = Math.min(minMipLevel, pageDescriptor.mipLevel)
                maxMipLevel = Math.max(maxMipLevel, pageDescriptor.mipLevel)
            })
            for (let mipLevel = minMipLevel; mipLevel <= maxMipLevel; mipLevel++) {
                const pageDescriptors = pagesByMipLevel.get(mipLevel)
                if (pageDescriptors) {
                    this.logger.log(
                        `      Mip level ${mipLevel} (${pageDescriptors.length} virtual pages / ${pageDescriptors.length * stats.numAtlasPagesPerVirtualPage} atlas pages): `,
                        pageDescriptors,
                    )
                }
            }
            if (stats.lockedPages.length > 0) {
                this.logger.log(`    !!! WARNING !!! Locked pages: `, stats.lockedPages)
            }
        }
    }

    freeAllPages() {
        if (this._outputImagePtr instanceof ImageOpHalOutputImageVirtual) {
            this._outputImagePtr.freeAllNonLockedPages()
        }
    }

    private collectSourceImages() {
        const sourceImages = new Set<AnyImageOpHalData>()
        traverse(this.parameters, (value) => {
            if (value instanceof ImageOpHalData) {
                sourceImages.add(value)
            }
        })
        return Array.from(sourceImages)
    }

    private setSourceImages(sourceImages: AnyImageOpHalData[]) {
        for (const newSourceImage of sourceImages) {
            let info = this._infoBySourceImage.get(newSourceImage)
            if (!info) {
                info = {
                    refCount: 1,
                    subscriptions: [
                        // newSourceImage.regionsInvalidated.subscribe((sourceRegions) => this.onSourceRegionsInvalidatedWithCheck(newSourceImage, sourceRegions)),
                    ],
                }
                newSourceImage.addRef("ImageOpFnHalData.sourceImage") // we keep a reference to all source images
                newSourceImage.addConsumer(this)
                this._infoBySourceImage.set(newSourceImage, info)
            } else {
                info.refCount++
            }
        }
        for (const oldSourceImage of this._sourceImages) {
            const info = this._infoBySourceImage.get(oldSourceImage)
            if (!info) {
                throw new Error("No info found for source image")
            }
            info.refCount--
            if (info.refCount === 0) {
                info.subscriptions.forEach((subscription) => subscription.unsubscribe())
                this._infoBySourceImage.delete(oldSourceImage)
                oldSourceImage.removeConsumer(this)
                oldSourceImage.release("ImageOpFnHalData.sourceImage") // release the kept reference to the source image
            }
        }
        this._sourceImages = sourceImages
    }

    get sourceImages() {
        return this._sourceImages
    }

    private static printPendingPromises() {
        ImageOpHalData.pendingPromises.forEach((info) => {
            ImageOpHalData.logger.log(`Pending promise for ${info.imageOpHalData.constructor.name} ${info.imageOpHalData.debugId}`, info.mipLevelRegions)
        })
    }

    private static addInstance(instance: AnyImageOpHalData) {
        if (ENABLE_DEBUG_INFO) {
            ImageOpHalData.instances.add(instance)
            ImageOpHalData.logger.trace(`Creating ImageOpHalData ${instance.constructor.name}`, instance)
        }
    }

    private static removeInstance(instance: AnyImageOpHalData) {
        if (ENABLE_DEBUG_INFO) {
            ImageOpHalData.instances.delete(instance)
            ImageOpHalData.logger.trace(`Disposing ImageOpHalData ${instance.debugId}`, instance)
        }
    }

    static printDebugInfo() {
        this.logger.log(`=== ${ImageOpHalData.instances.size} ImageOpHalData Instances ===`)
        ImageOpHalData.instances.forEach((instance) => {
            ImageOpHalData.logger.log(
                `  *** ${instance.debugId ?? "unnamed"} (RefCount: ${instance.refCount}) ***`,
                instance,
                Array.from(instance.refCountsByDebugId.entries()),
            )
        })
        ImageOpHalData.printPendingPromises()
    }

    readonly ctx: ImageOpContextHal
    readonly parameters: ImageOpHalDataParameters<Definition>
    readonly descriptor: Descriptor
    readonly numMipLevels: number
    readonly consumers: Set<AnyImageOpHalData> = new Set()

    private _disposed = false
    private _usePhysicalOutputImage: boolean
    private _physicalOutputImageDataType: DataType
    private _outputImagePtr: ImageOpHalOutputImage | null

    private _updateComplexity: number // higher means more complex
    private _cachingStrategy: CachingStrategy = "auto"
    private _cachingIsUseful = true // whether the caching is useful (when the strategy is "auto" caching is turned based on this flag)

    private _sourceImages: AnyImageOpHalData[] = []
    private _infoBySourceImage = new Map<AnyImageOpHalData, SourceImageInfo>()

    private _initiallyInvalidRegions: Regions = []

    private _debugId?: string

    private static pendingPromises = new Set<PendingPromiseDebugInfo>()
    static instances = new Set<AnyImageOpHalData>()
    // TODO: remove static logger, replace with instance logger
    static readonly logger = loggerFactory.getLogger("image-op-system")
}

type SourceImageInfo = {
    refCount: number
    subscriptions: Subscription[]
}

type PendingPromiseDebugInfo = {
    promise: Promise<unknown>
    imageOpHalData: AnyImageOpHalData
    mipLevelRegions: MipLevelRegions
}
